"use client"

import * as React from "react"
import { createPortal } from "react-dom"
import { X, Info } from "lucide-react"
import { type PlanDisplayData } from "@/lib/plan-display-data"

interface PriceDetailModalProps {
  plan: PlanDisplayData
  onClose: () => void
}

function fmt(n: number, decimals = 2) {
  return n.toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })
}

function humanize(s: string) {
  return s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
}

export function PriceDetailModal({ plan, onClose }: PriceDetailModalProps) {
  const pb = plan.priceBreakdown
  const [mounted, setMounted] = React.useState(false)

  const fullMembers = React.useMemo(() => pb?.fullMembers ?? [], [pb])
  const loadingBreakdown = React.useMemo(() => pb?.loadingBreakdown ?? [], [pb])
  const [showAdjBreakdown, setShowAdjBreakdown] = React.useState(false)
  const [selectedMember, setSelectedMember] = React.useState<number | null>(null)

  React.useEffect(() => {
    setMounted(true)
  }, [])

  React.useEffect(() => {
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = "hidden"
    return () => {
      document.body.style.overflow = previousOverflow
    }
  }, [])

  // Collect all unique benefit types across all members
  const benefitTypes = React.useMemo(() => {
    const seen = new Set<string>()
    const types: Array<{ benefitType: string; label: string }> = []
    for (const m of fullMembers) {
      for (const ob of m.optionalBreakdown ?? []) {
        if (!seen.has(ob.benefitType)) {
          seen.add(ob.benefitType)
          types.push({ benefitType: ob.benefitType, label: ob.label ?? humanize(ob.benefitType) })
        }
      }
    }
    return types
  }, [fullMembers])

  // Benefit column totals
  const benefitTotals = React.useMemo(() => {
    const totals: Record<string, number> = {}
    for (const m of fullMembers) {
      for (const ob of m.optionalBreakdown ?? []) {
        totals[ob.benefitType] = (totals[ob.benefitType] ?? 0) + ob.rate
      }
    }
    return totals
  }, [fullMembers])

  if (!pb) return null

  const currency = pb.currency ?? "AED"
  const grandTotalNet = fullMembers.reduce((s, m) => s + (m.netRate ?? 0), 0)
  const totalLoadingPct = loadingBreakdown.filter((l) => l.party !== 'trudoc').reduce((s, l) => s + l.percentage, 0)
  const grandTotalGross = fullMembers.reduce((s, m) => s + (m.grossPremium ?? 0), 0)
  const totalTrudoc = fullMembers.reduce((s, m) => s + (m.memberFees?.trudoc ?? 0), 0)
  const grandTotalNetInclusive = fullMembers.reduce((s, m) => s + (m.netRateInclusive ?? (m.netRate + (m.memberFees?.trudoc ?? 0))), 0)
  const grandTotalWithVat = pb.totalWithVat ?? 0
  const vat = pb.vat ?? 0
  const basmah = pb.basmah ?? 0
  const icp = pb.icp ?? 0
  const dynamicFees = pb.fees ?? []
  const totalFees = dynamicFees.length > 0
    ? dynamicFees.filter(f => f.feeType !== 'trudoc').reduce((s, f) => s + f.amount, 0)
    : basmah + icp
  const totalIncludingFees = grandTotalGross + totalFees
  const totalAdditionalPremium = Object.values(benefitTotals).reduce((s, v) => s + v, 0)
  const loadingsAmount = (grandTotalNetInclusive * totalLoadingPct) / 100

  if (!mounted) return null

  return createPortal(
    <>
      <div
        className="fixed inset-0 z-[100] min-h-svh bg-black/60"
        onClick={onClose}
        aria-hidden
      />

      <div className="pointer-events-none fixed inset-0 z-[100] overflow-y-auto p-4 pt-10">
        <div
          className="relative mx-auto mb-4 w-full max-w-[1400px] pointer-events-auto rounded-2xl bg-white shadow-2xl dark:bg-slate-900"
          onClick={(e) => e.stopPropagation()}
        >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-6 py-4 dark:border-slate-700 dark:bg-slate-800/60">
          <div>
            <h2 className="text-base font-bold text-slate-900 dark:text-white">
              Price Detail — {pb.planName ?? plan.name}
              {pb.benefitCategory ? ` · Category ${pb.benefitCategory}` : ""}
            </h2>
            <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
              TPA: {pb.tpa ?? plan.tpa ?? "—"} · Currency: {currency}
              {pb.policyStart ? ` · Policy Start: ${pb.policyStart}` : ""}
            </p>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-200 hover:text-slate-700 dark:hover:bg-slate-700 dark:hover:text-white"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Loading summary */}
          {loadingBreakdown.filter((l) => l.party !== 'trudoc').length > 0 && (
            <div className="flex flex-wrap gap-4 rounded-xl border border-slate-200 bg-slate-50 px-5 py-3 dark:border-slate-700 dark:bg-slate-800/40">
              {loadingBreakdown.filter((l) => l.party !== 'trudoc').map((l) => {
                const roundedPct = Math.round(l.percentage * 100) / 100
                return (
                  <div key={l.party} className="text-xs">
                    <span className="font-semibold text-slate-700 dark:text-slate-200 capitalize">{l.party}</span>
                    <span className="ml-1 text-slate-500">{roundedPct}%</span>
                  </div>
                )
              })}
              <div className="text-xs">
                <span className="font-semibold text-slate-700 dark:text-slate-200">Total Loading</span>
                <span className="ml-1 text-slate-500">{Math.round(totalLoadingPct * 100) / 100}%</span>
              </div>
            </div>
          )}

          {/* Main member table */}
          {fullMembers.length > 0 && (
            <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-700">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-800 text-white dark:border-slate-700">
                    <th className="px-3 py-2.5 text-left font-semibold whitespace-nowrap">#</th>
                    <th className="px-3 py-2.5 text-left font-semibold whitespace-nowrap">Full Name</th>
                    <th className="px-3 py-2.5 text-left font-semibold whitespace-nowrap">Date of Birth</th>
                    <th className="px-3 py-2.5 text-left font-semibold whitespace-nowrap">Gender</th>
                    <th className="px-3 py-2.5 text-left font-semibold whitespace-nowrap">Relationship</th>
                    <th className="px-3 py-2.5 text-center font-semibold whitespace-nowrap">Age</th>
                    <th className="px-3 py-2.5 text-left font-semibold whitespace-nowrap">Marital Status</th>
                    <th className="px-3 py-2.5 text-left font-semibold whitespace-nowrap">Visa Emirate</th>
                    <th className="px-3 py-2.5 text-left font-semibold whitespace-nowrap">Benefit Category</th>
                    <th className="px-3 py-2.5 text-right font-semibold whitespace-nowrap bg-amber-700">Base Premium</th>
                    <th className="px-3 py-2.5 text-right font-semibold whitespace-nowrap">Adj. Factor</th>
                    <th className="px-3 py-2.5 text-right font-semibold whitespace-nowrap">Adj. Breakdown</th>
                    <th className="px-3 py-2.5 text-right font-semibold whitespace-nowrap bg-amber-600">Modified Base</th>
                    {benefitTypes.map((bt) => (
                      <th key={bt.benefitType} className="px-3 py-2.5 text-right font-semibold whitespace-nowrap bg-orange-600">
                        {bt.label}
                      </th>
                    ))}
                    <th className="px-3 py-2.5 text-right font-semibold whitespace-nowrap">Total Add. Premium</th>
                    <th className="px-3 py-2.5 text-right font-semibold whitespace-nowrap bg-green-700">Total Net Rate</th>
                    <th className="px-3 py-2.5 text-right font-semibold whitespace-nowrap bg-teal-700">Tru Doc</th>
                    <th className="px-3 py-2.5 text-right font-semibold whitespace-nowrap bg-teal-800">Net Inclusive</th>
                    <th className="px-3 py-2.5 text-right font-semibold whitespace-nowrap bg-green-800">Total Gross</th>
                  </tr>
                </thead>
                <tbody>
                  {fullMembers.map((m, i) => {
                    const memberBenefitTotal = (m.optionalBreakdown ?? []).reduce(
                      (s, ob) => s + ob.rate,
                      0
                    )
                    return (
                      <tr
                        key={i}
                        className={
                          i % 2 === 0
                            ? "bg-white dark:bg-slate-900"
                            : "bg-slate-50 dark:bg-slate-800/40"
                        }
                      >
                        <td className="px-3 py-2 text-slate-500">{i + 1}</td>
                        <td className="px-3 py-2 font-medium text-slate-800 dark:text-slate-200 whitespace-nowrap">
                          {m.name}
                        </td>
                        <td className="px-3 py-2 text-slate-600 dark:text-slate-400 whitespace-nowrap">
                          {m.dob ? new Date(m.dob).toLocaleDateString("en-GB") : "—"}
                        </td>
                        <td className="px-3 py-2 text-slate-600 dark:text-slate-400 capitalize">
                          {m.gender}
                        </td>
                        <td className="px-3 py-2 text-slate-600 dark:text-slate-400 capitalize">
                          {m.relationship}
                        </td>
                        <td className="px-3 py-2 text-center text-slate-600 dark:text-slate-400">
                          {m.age}
                        </td>
                        <td className="px-3 py-2 text-slate-600 dark:text-slate-400 capitalize">
                          {m.maritalStatus ?? "—"}
                        </td>
                        <td className="px-3 py-2 text-slate-600 dark:text-slate-400">
                          {m.visaEmirate ?? "—"}
                        </td>
                        <td className="px-3 py-2 text-slate-600 dark:text-slate-400">
                          {pb.benefitCategory ?? "—"}
                        </td>
                        <td className="px-3 py-2 text-right font-mono font-semibold text-slate-800 dark:text-slate-100 bg-amber-50 dark:bg-amber-900/20">
                          {fmt(m.baseRate)}
                        </td>
                        <td className="px-3 py-2 text-right font-mono text-slate-600 dark:text-slate-400">
                          {(m.adjustmentFactor * 100).toFixed(2)}%
                        </td>
                        <td className="px-3 py-2 text-right">
                          {m.adjustmentBreakdown && m.adjustmentBreakdown.length > 0 ? (
                            <button
                              onClick={() => {
                                setSelectedMember(i)
                                setShowAdjBreakdown(true)
                              }}
                              className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                            >
                              <Info className="h-3 w-3" />
                              {m.adjustmentBreakdown.length} factor{m.adjustmentBreakdown.length !== 1 ? 's' : ''}
                            </button>
                          ) : (
                            <span className="text-slate-400">—</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right font-mono font-semibold text-slate-800 dark:text-slate-100 bg-amber-50/60 dark:bg-amber-900/10">
                          {fmt(m.modifiedBase)}
                        </td>
                        {benefitTypes.map((bt) => {
                          const ob = (m.optionalBreakdown ?? []).find(
                            (o) => o.benefitType === bt.benefitType
                          )
                          return (
                            <td
                              key={bt.benefitType}
                              className="px-3 py-2 text-right font-mono text-slate-600 dark:text-slate-400 bg-orange-50/40 dark:bg-orange-900/10"
                            >
                              {ob && ob.rate > 0 ? fmt(ob.rate) : "—"}
                            </td>
                          )
                        })}
                        <td className="px-3 py-2 text-right font-mono font-semibold text-slate-800 dark:text-slate-200">
                          {fmt(memberBenefitTotal)}
                        </td>
                        <td className="px-3 py-2 text-right font-mono font-bold text-emerald-700 dark:text-emerald-400 bg-green-50 dark:bg-green-900/20">
                          {fmt(m.netRate)}
                        </td>
                        <td className="px-3 py-2 text-right font-mono text-teal-700 dark:text-teal-400 bg-teal-50 dark:bg-teal-900/20">
                          {m.memberFees?.trudoc ? fmt(m.memberFees.trudoc) : "—"}
                        </td>
                        <td className="px-3 py-2 text-right font-mono font-semibold text-teal-800 dark:text-teal-300 bg-teal-100 dark:bg-teal-900/30">
                          {fmt(m.netRateInclusive ?? (m.netRate + (m.memberFees?.trudoc ?? 0)))}
                        </td>
                        <td className="px-3 py-2 text-right font-mono font-bold text-slate-900 dark:text-white bg-green-100 dark:bg-green-900/30">
                          {fmt(m.grossPremium ?? 0)}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-slate-300 bg-slate-100 font-bold dark:border-slate-600 dark:bg-slate-800">
                    <td colSpan={9} className="px-3 py-2.5 text-xs font-bold text-slate-700 dark:text-slate-200">
                      TOTALS
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono text-amber-800 dark:text-amber-300 bg-amber-100 dark:bg-amber-900/30">
                      {fmt(fullMembers.reduce((s, m) => s + m.baseRate, 0))}
                    </td>
                    <td className="px-3 py-2.5 text-right text-slate-500">—</td>
                    <td className="px-3 py-2.5 text-right text-slate-500">—</td>
                    <td className="px-3 py-2.5 text-right font-mono text-amber-800 dark:text-amber-300 bg-amber-100/60 dark:bg-amber-900/20">
                      {fmt(fullMembers.reduce((s, m) => s + m.modifiedBase, 0))}
                    </td>
                    {benefitTypes.map((bt) => (
                      <td key={bt.benefitType} className="px-3 py-2.5 text-right font-mono text-orange-700 dark:text-orange-300 bg-orange-100/40 dark:bg-orange-900/20">
                        {fmt(benefitTotals[bt.benefitType] ?? 0)}
                      </td>
                    ))}
                    <td className="px-3 py-2.5 text-right font-mono text-slate-800 dark:text-slate-100">
                      {fmt(totalAdditionalPremium)}
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono font-bold text-emerald-700 dark:text-emerald-300 bg-green-100 dark:bg-green-900/30">
                      {fmt(grandTotalNet)}
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono text-teal-700 dark:text-teal-300 bg-teal-100 dark:bg-teal-900/30">
                      {fmt(totalTrudoc)}
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono font-bold text-teal-800 dark:text-teal-300 bg-teal-200 dark:bg-teal-900/40">
                      {fmt(grandTotalNetInclusive)}
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono font-bold text-slate-900 dark:text-white bg-green-200 dark:bg-green-900/40">
                      {fmt(grandTotalGross)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}

          {/* Summary panel */}
          <div className="grid grid-cols-2 gap-6 sm:grid-cols-3">
            {/* Net/Gross/VAT totals */}
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-800/40">
              <h3 className="mb-3 text-[11px] font-bold tracking-wide text-slate-500 uppercase dark:text-slate-400">
                Premium Summary
              </h3>
              <dl className="space-y-1.5 text-xs">
                <div className="flex justify-between">
                  <dt className="text-slate-500 dark:text-slate-400">Total Net</dt>
                  <dd className="font-semibold font-mono text-slate-800 dark:text-slate-200">{currency} {fmt(grandTotalNet)}</dd>
                </div>
                {totalTrudoc > 0 && (
                  <div className="flex justify-between">
                    <dt className="text-slate-500 dark:text-slate-400">Tru Doc ({fullMembers.filter(m => m.memberFees?.trudoc).length} members)</dt>
                    <dd className="font-semibold font-mono text-teal-700 dark:text-teal-400">{currency} {fmt(totalTrudoc)}</dd>
                  </div>
                )}
                {totalTrudoc > 0 && (
                  <div className="flex justify-between">
                    <dt className="text-slate-500 dark:text-slate-400">Net Inclusive</dt>
                    <dd className="font-semibold font-mono text-teal-800 dark:text-teal-300">{currency} {fmt(grandTotalNetInclusive)}</dd>
                  </div>
                )}
                <div className="flex justify-between border-t border-slate-200 pt-1.5 dark:border-slate-700">
                  <dt className="text-slate-700 dark:text-slate-200 font-semibold">Total Gross (incl. {totalLoadingPct}% loadings)</dt>
                  <dd className="font-bold font-mono text-slate-900 dark:text-white">{currency} {fmt(grandTotalGross)}</dd>
                </div>
                {dynamicFees.filter(f => f.feeType !== 'trudoc').length > 0 ? (
                  dynamicFees.filter(f => f.feeType !== 'trudoc').map((fee) => (
                    <div key={fee.feeType} className="flex justify-between">
                      <dt className="text-slate-500 dark:text-slate-400">
                        {fee.feeType.charAt(0).toUpperCase() + fee.feeType.slice(1)}
                        {fee.memberCount ? ` (${fee.memberCount} members)` : ""}
                      </dt>
                      <dd className="font-semibold font-mono text-slate-800 dark:text-slate-200">{currency} {fmt(fee.amount)}</dd>
                    </div>
                  ))
                ) : (
                  <>
                    {basmah > 0 && (
                      <div className="flex justify-between">
                        <dt className="text-slate-500 dark:text-slate-400">
                          Basmah{pb.basmahMemberCount ? ` (${pb.basmahMemberCount} members)` : ""}
                        </dt>
                        <dd className="font-semibold font-mono text-slate-800 dark:text-slate-200">{currency} {fmt(basmah)}</dd>
                      </div>
                    )}
                    {icp > 0 && (
                      <div className="flex justify-between">
                        <dt className="text-slate-500 dark:text-slate-400">
                          ICP{pb.icpMemberCount ? ` (${pb.icpMemberCount} members)` : ""}
                        </dt>
                        <dd className="font-semibold font-mono text-slate-800 dark:text-slate-200">{currency} {fmt(icp)}</dd>
                      </div>
                    )}
                  </>
                )}
                {totalFees > 0 && (
                  <div className="flex justify-between">
                    <dt className="text-slate-700 dark:text-slate-200 font-semibold">Incl. Fees</dt>
                    <dd className="font-bold font-mono text-slate-900 dark:text-white">{currency} {fmt(totalIncludingFees)}</dd>
                  </div>
                )}
                <div className="flex justify-between border-t border-slate-200 pt-1.5 dark:border-slate-700">
                  <dt className="text-slate-500 dark:text-slate-400">VAT (5%)</dt>
                  <dd className="font-semibold font-mono text-slate-800 dark:text-slate-200">{currency} {fmt(vat)}</dd>
                </div>
                <div className="flex justify-between border-t-2 border-nova-500 pt-1.5">
                  <dt className="text-nova-700 dark:text-nova-300 font-bold text-sm">Total Payable</dt>
                  <dd className="font-bold font-mono text-nova-700 dark:text-nova-300 text-sm">{currency} {fmt(grandTotalWithVat)}</dd>
                </div>
              </dl>
            </div>

            {/* Per-benefit totals */}
            {benefitTypes.length > 0 && (
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-800/40">
                <h3 className="mb-3 text-[11px] font-bold tracking-wide text-slate-500 uppercase dark:text-slate-400">
                  Benefit-wise Additional Premium
                </h3>
                <dl className="space-y-1.5 text-xs">
                  {benefitTypes.map((bt) => (
                    <div key={bt.benefitType} className="flex justify-between">
                      <dt className="text-slate-500 dark:text-slate-400">{bt.label}</dt>
                      <dd className="font-semibold font-mono text-slate-800 dark:text-slate-200">
                        {fmt(benefitTotals[bt.benefitType] ?? 0)}
                      </dd>
                    </div>
                  ))}
                  <div className="flex justify-between border-t border-slate-200 pt-1.5 dark:border-slate-700">
                    <dt className="font-semibold text-slate-700 dark:text-slate-200">Total Additional</dt>
                    <dd className="font-bold font-mono text-slate-900 dark:text-white">{fmt(totalAdditionalPremium)}</dd>
                  </div>
                </dl>
              </div>
            )}

            {/* Loading breakdown */}
            {loadingBreakdown.filter((l) => l.party !== 'trudoc').length > 0 && (
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-800/40">
                <h3 className="mb-3 text-[11px] font-bold tracking-wide text-slate-500 uppercase dark:text-slate-400">
                  Loading Breakdown
                </h3>
                <dl className="space-y-1.5 text-xs">
                  {loadingBreakdown.filter((l) => l.party !== 'trudoc').map((l) => {
                    const amount = (grandTotalNetInclusive * l.percentage) / 100
                    const roundedPercentage = Math.round(l.percentage * 100) / 100
                    return (
                      <div key={l.party} className="flex justify-between">
                        <dt className="text-slate-500 dark:text-slate-400 capitalize">{l.party}</dt>
                        <dd className="font-mono text-slate-700 dark:text-slate-300">
                          {roundedPercentage}%
                          <span className="ml-2 text-slate-500">({fmt(amount)})</span>
                        </dd>
                      </div>
                    )
                  })}
                  <div className="flex justify-between border-t border-slate-200 pt-1.5 dark:border-slate-700">
                    <dt className="font-semibold text-slate-700 dark:text-slate-200">Total Loadings</dt>
                    <dd className="font-bold font-mono text-slate-900 dark:text-white">{fmt(loadingsAmount)}</dd>
                  </div>
                </dl>
              </div>
            )}
          </div>

          {/* Per-member pricing chain (expandable) */}
          {(pb.pricingMembers ?? []).length > 0 && (
            <details className="rounded-xl border border-slate-200 dark:border-slate-700">
              <summary className="cursor-pointer rounded-xl px-5 py-3 text-xs font-semibold text-slate-600 hover:bg-slate-50 dark:text-slate-400 dark:hover:bg-slate-800/40">
                ▶ Per-member calculation chain (base × adj = modified + optional = net)
              </summary>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-t border-slate-200 bg-slate-100 dark:border-slate-700 dark:bg-slate-800">
                      <th className="px-3 py-2 text-left font-semibold text-slate-600 dark:text-slate-300">Member</th>
                      <th className="px-3 py-2 text-right font-semibold text-slate-600 dark:text-slate-300">Base Rate</th>
                      <th className="px-3 py-2 text-right font-semibold text-slate-600 dark:text-slate-300">× Adj</th>
                      <th className="px-3 py-2 text-right font-semibold text-slate-600 dark:text-slate-300">= Modified</th>
                      <th className="px-3 py-2 text-right font-semibold text-slate-600 dark:text-slate-300">+ Optional</th>
                      <th className="px-3 py-2 text-right font-semibold text-slate-600 dark:text-slate-300">= Net Rate</th>
                      <th className="px-3 py-2 text-right font-semibold text-slate-600 dark:text-slate-300">+ Tru Doc</th>
                      <th className="px-3 py-2 text-right font-semibold text-slate-600 dark:text-slate-300">= Net Inclusive</th>
                      <th className="px-3 py-2 text-right font-semibold text-slate-600 dark:text-slate-300">+ Basma/ICP</th>
                      <th className="px-3 py-2 text-right font-semibold text-slate-600 dark:text-slate-300">Final</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(pb.pricingMembers ?? []).map((m, i) => (
                      <tr key={i} className={i % 2 === 0 ? "bg-white dark:bg-slate-900" : "bg-slate-50 dark:bg-slate-800/40"}>
                        <td className="px-3 py-2 font-medium text-slate-700 dark:text-slate-300">
                          {m.name}
                          <span className="ml-1 text-[10px] text-slate-400">
                            {[m.relationship, m.age != null ? `${m.age}y` : null, m.gender]
                              .filter(Boolean)
                              .join(" · ")}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-right font-mono text-slate-600 dark:text-slate-400">{fmt(m.baseRate)}</td>
                        <td className="px-3 py-2 text-right font-mono text-slate-500 dark:text-slate-500">{(m.adjustmentFactor * 100).toFixed(2)}%</td>
                        <td className="px-3 py-2 text-right font-mono text-slate-700 dark:text-slate-300">{fmt(m.modifiedBase)}</td>
                        <td className="px-3 py-2 text-right font-mono text-orange-600 dark:text-orange-400">{fmt(m.optionalPremium)}</td>
                        <td className="px-3 py-2 text-right font-mono font-semibold text-emerald-700 dark:text-emerald-400">{fmt(m.netRate)}</td>
                        <td className="px-3 py-2 text-right font-mono text-teal-600 dark:text-teal-400">{m.memberFees?.trudoc ? fmt(m.memberFees.trudoc) : "—"}</td>
                        <td className="px-3 py-2 text-right font-mono font-semibold text-teal-700 dark:text-teal-300">{fmt(m.netRateInclusive ?? (m.netRate + (m.memberFees?.trudoc ?? 0)))}</td>
                        <td className="px-3 py-2 text-right font-mono text-slate-500 dark:text-slate-500">{fmt(m.basmaOrIcp)}</td>
                        <td className="px-3 py-2 text-right font-mono font-bold text-slate-900 dark:text-white">{fmt(m.finalPremium)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          )}
        </div>

        <div className="flex justify-end border-t border-slate-200 px-6 py-3 dark:border-slate-700">
          <button
            onClick={onClose}
            className="rounded-lg bg-slate-800 px-5 py-2 text-sm font-semibold text-white hover:bg-slate-700"
          >
            Close
          </button>
        </div>
        </div>
      </div>

      {/* Adjustment Breakdown Modal */}
      {showAdjBreakdown && selectedMember !== null && fullMembers[selectedMember] && (
        <>
          <div
            className="fixed inset-0 z-[110] min-h-svh bg-black/60"
            onClick={() => setShowAdjBreakdown(false)}
            aria-hidden
          />
          <div className="pointer-events-none fixed inset-0 z-[110] flex items-center justify-center p-4">
            <div
              className="relative w-full max-w-lg pointer-events-auto rounded-2xl bg-white shadow-2xl dark:bg-slate-900"
              onClick={(e) => e.stopPropagation()}
            >
            <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-6 py-4 dark:border-slate-700 dark:bg-slate-800/60">
              <div>
                <h3 className="text-sm font-bold text-slate-900 dark:text-white">
                  Adjustment Factor Breakdown
                </h3>
                <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                  {fullMembers[selectedMember].name} · {fullMembers[selectedMember].relationship}
                </p>
              </div>
              <button
                onClick={() => setShowAdjBreakdown(false)}
                className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-200 hover:text-slate-700 dark:hover:bg-slate-700 dark:hover:text-white"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-6">
              <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-700">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-100 dark:border-slate-700 dark:bg-slate-800">
                      <th className="px-3 py-2 text-left font-semibold text-slate-600 dark:text-slate-300">Factor Type</th>
                      <th className="px-3 py-2 text-left font-semibold text-slate-600 dark:text-slate-300">Option</th>
                      <th className="px-3 py-2 text-right font-semibold text-slate-600 dark:text-slate-300">Multiplier</th>
                      <th className="px-3 py-2 text-center font-semibold text-slate-600 dark:text-slate-300">Default</th>
                    </tr>
                  </thead>
                  <tbody>
                    {fullMembers[selectedMember].adjustmentBreakdown?.map((ab, i) => (
                      <tr
                        key={i}
                        className={i % 2 === 0 ? "bg-white dark:bg-slate-900" : "bg-slate-50 dark:bg-slate-800/40"}
                      >
                        <td className="px-3 py-2 font-medium text-slate-700 dark:text-slate-300">
                          {humanize(ab.factorType)}
                        </td>
                        <td className="px-3 py-2 text-slate-600 dark:text-slate-400">
                          {ab.optionLabel}
                        </td>
                        <td className="px-3 py-2 text-right font-mono text-slate-600 dark:text-slate-400">
                          {Number(ab.multiplier).toFixed(4)}
                        </td>
                        <td className="px-3 py-2 text-center text-slate-500 dark:text-slate-400">
                          {ab.isDefault ? "✓" : ""}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {fullMembers[selectedMember].adjustmentBreakdown && fullMembers[selectedMember].adjustmentBreakdown.length > 0 && (
                <div className="mt-4 rounded-lg bg-slate-50 px-4 py-3 dark:bg-slate-800/40">
                  <p className="text-xs text-slate-600 dark:text-slate-400">
                    <span className="font-semibold">Calculation:</span>{" "}
                    100% + (
                    {fullMembers[selectedMember].adjustmentBreakdown
                      .map((ab) => {
                        const pct = (Number(ab.multiplier) - 1) * 100
                        const formatted = `${Math.abs(pct).toFixed(2)}%`
                        return pct >= 0 ? `+${formatted}` : `−${formatted}`
                      })
                      .join(" ")}
                    ) ={" "}
                    <span className="font-mono font-semibold text-slate-800 dark:text-slate-200">
                      {(Number(fullMembers[selectedMember].adjustmentFactor) * 100).toFixed(2)}%
                    </span>
                    {" "}
                    <span className="font-mono text-slate-500 dark:text-slate-400">
                      ({Number(fullMembers[selectedMember].adjustmentFactor).toFixed(4)})
                    </span>
                  </p>
                </div>
              )}
            </div>
            <div className="flex justify-end border-t border-slate-200 px-6 py-3 dark:border-slate-700">
              <button
                onClick={() => setShowAdjBreakdown(false)}
                className="rounded-lg bg-slate-800 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-700"
              >
                Close
              </button>
            </div>
            </div>
          </div>
        </>
      )}
    </>,
    document.body
  )
}
